import { useState, useMemo } from "react";
import AppLayout from "@/components/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Map, Zap, BarChart3, MapPin, Target, TrendingUp, Clock, Mic, Square, Play } from "lucide-react";
import { useCanvassing } from "@/hooks/useCanvassing";
import { CanvassingMapView } from "@/components/canvassing/CanvassingMapView";
import { CanvassingSessionPanel } from "@/components/canvassing/CanvassingSessionPanel";
import { CanvassingLeaderboard } from "@/components/canvassing/CanvassingLeaderboard";
import { CanvassingTerritories } from "@/components/canvassing/CanvassingTerritories";
import { CanvassingStormTrigger } from "@/components/canvassing/CanvassingStormTrigger";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

export default function CanvassingHub() {
  const [activeTab, setActiveTab] = useState("map");
  const canvassing = useCanvassing();
  const { activeSession, pins } = canvassing;

  // Live session-only stats
  const sessionStats = useMemo(() => {
    if (!activeSession) {
      return { knocked: 0, appts: 0, leads: 0, conv: 0 };
    }
    const sessionPins = pins.filter(p => p.session_id === activeSession.id);
    const knocked = activeSession.doors_knocked || sessionPins.length;
    const appts = activeSession.appointments_set || sessionPins.filter(p => p.outcome === "appointment_set" || p.outcome === "sold").length;
    const leads = activeSession.leads_created || 0;
    const conv = knocked > 0 ? Math.round((appts / knocked) * 100) : 0;
    return { knocked, appts, leads, conv };
  }, [activeSession, pins]);

  const askBridgette = () => {
    window.dispatchEvent(new CustomEvent("bridgette-open"));
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Hero header */}
        <div className="px-4 sm:px-6 py-4 border-b border-border bg-background">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground">Canvassing Hub</h1>
                {activeSession && (
                  <Badge className="gap-1.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    Live · {formatDistanceToNow(new Date(activeSession.started_at))}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {activeSession
                  ? "Tap the map (or the Log Door button) to record every door."
                  : "Pick a territory or freestyle — start a session to begin tracking."}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={askBridgette} className="gap-1.5">
                <Mic className="w-4 h-4" />
                <span className="hidden sm:inline">Ask Bridgette</span>
              </Button>
              <CanvassingSessionPanel
                activeSession={activeSession}
                onStartSession={canvassing.startSession}
                onEndSession={canvassing.endSession}
                territories={canvassing.territories}
              />
            </div>
          </div>

          {/* Live session stat bar */}
          {activeSession && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              <StatCard icon={MapPin} label="Doors knocked" value={sessionStats.knocked} tone="primary" />
              <StatCard icon={Target} label="Appointments" value={sessionStats.appts} tone="success" />
              <StatCard icon={TrendingUp} label="Leads created" value={sessionStats.leads} tone="warning" />
              <StatCard icon={Clock} label="Conversion" value={`${sessionStats.conv}%`} tone="accent" />
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 sm:px-6 pt-2 border-b border-border bg-background">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="map" className="gap-1.5">
                <Map className="w-4 h-4" /> Map
              </TabsTrigger>
              <TabsTrigger value="territories" className="gap-1.5">
                <MapPin className="w-4 h-4" /> Territories
              </TabsTrigger>
              <TabsTrigger value="leaderboard" className="gap-1.5">
                <BarChart3 className="w-4 h-4" /> Leaderboard
              </TabsTrigger>
              <TabsTrigger value="storm" className="gap-1.5">
                <Zap className="w-4 h-4" /> Storm Triggers
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="map" className="flex-1 m-0 overflow-hidden">
            <CanvassingMapView
              pins={pins}
              territories={canvassing.territories}
              activeSession={activeSession}
              onAddPin={canvassing.addPin}
              onUpdatePin={canvassing.updatePinOutcome}
              onDeletePin={canvassing.deletePin}
              onAddBreadcrumb={canvassing.addBreadcrumb}
              onAskBridgette={askBridgette}
            />
          </TabsContent>

          <TabsContent value="territories" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            <CanvassingTerritories
              territories={canvassing.territories}
              pins={pins}
              onCreateTerritory={canvassing.createTerritory}
            />
          </TabsContent>

          <TabsContent value="leaderboard" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            <CanvassingLeaderboard sessions={canvassing.sessions} pins={pins} />
          </TabsContent>

          <TabsContent value="storm" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            <CanvassingStormTrigger onCreateTerritory={canvassing.createTerritory} />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: any;
  label: string;
  value: string | number;
  tone: "primary" | "success" | "warning" | "accent";
}) {
  const toneClasses: Record<string, string> = {
    primary: "bg-primary/10 text-primary border-primary/20",
    success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    accent: "bg-secondary/15 text-secondary-foreground border-secondary/30",
  };
  return (
    <Card className={cn("p-3 flex items-center gap-3 border", toneClasses[tone])}>
      <div className="p-2 rounded-lg bg-background/60">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-bold leading-tight">{value}</div>
        <div className="text-[11px] uppercase tracking-wide opacity-80 truncate">{label}</div>
      </div>
    </Card>
  );
}
